import { Fragment, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import "./Items.css";
import "./OrderCostAnalysis.css";

/**
 * SİPARİŞ MALİ ANALİZİ — maliyet & kâr (yalnız süper-admin).
 *
 * Kullanıcı isteği (29 Ağu 2026): "siparişte kullanılacak tüm reçete kalemlerini tek tek yazıp
 * reçete gruplarının da kırılımlarını vererek, birim fiyatları ile birlikte sipariş miktarlarına
 * göre toplam bir maliyet analizi… maliyet-kâr analizi yapılsın."
 *
 * ⚠ EKSİK HESAP GİZLENMEZ: fiyatı olmayan ham madde ya da reçetesiz ürün varsa maliyet ALT
 * SINIRDIR ve kâr OLDUĞUNDAN YÜKSEK görünür. Bu, mali kararda en tehlikeli hata olduğu için
 * sayfanın en üstünde, kırmızı ve sayıyla söylenir — kartlar da "eksik" damgası taşır.
 *
 * ⚠ PARA BİRİMİ: maliyet TRY (alış fiyat listesi), satış siparişin kendi para biriminde.
 * Farklı para biriminde kâr OTOMATİK hesaplanmaz; kullanıcı kur girerse BİLGİ AMAÇLI gösterilir
 * (teslimat planındaki desenin aynısı — CLAUDE.md: dövizli tutar tek rakama indirgenmez).
 */

const fmtPara = (n: number) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtMiktar = (n: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
const fmtYuzde = (n: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(n);
const SEMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

interface Dugum {
  anahtar: string; seviye: number; itemKind: string; itemId: string; code: string; name: string;
  uom: string; miktar: number; birimFiyat: number | null; tutar: number | null;
  grupMu: boolean; eksikFiyat: boolean; cocuklar: Dugum[];
}
interface Satir {
  orderLineId: string; itemKind: string; code: string; name: string; uom: string; miktar: number;
  satisBirimFiyat: number | null; satisParaBirimi: string | null; satisTutar: number | null;
  maliyetBirim: number | null; maliyetToplam: number | null;
  karTutar: number | null; karYuzde: number | null;
  agac: Dugum[];
  fiyatsizKalemler: Array<{ code: string; name: string; uom: string; miktar: number; recetesiz: boolean }>;
  recetesiz: boolean;
}
interface Analiz {
  siparis: { id: string; code: string; status: string; musteri: string };
  maliyetTabani: string; maliyetParaBirimi: string;
  satirlar: Satir[];
  satisToplam: Array<{ paraBirimi: string; tutar: number }>;
  maliyetToplam: number; tryKar: number | null;
  fiyatsizKalemler: Array<{ code: string; name: string; uom: string; miktar: number; recetesiz: boolean }>;
  eksikHesap: boolean; uretildiginde: string;
}

/** Ağacı düz satırlara açar — girinti `seviye` ile verilir (yuvalı tablo baskıda bozuluyor). */
function duzles(nodes: Dugum[], out: Dugum[] = []): Dugum[] {
  for (const n of nodes) { out.push(n); if (n.cocuklar.length) duzles(n.cocuklar, out); }
  return out;
}

export function OrderCostAnalysisPage() {
  const { id = "" } = useParams();
  const q = trpc.order.costAnalysis.useQuery({ orderId: id }, { enabled: !!id, staleTime: 60_000 });
  const veri = q.data as unknown as Analiz | undefined;
  /** Satır bazında kırılım açık mı — varsayılan KAPALI (derin reçetede yüzlerce satır olabilir). */
  const [acik, setAcik] = useState<Record<string, boolean>>({});
  /** Manuel kur: döviz → TL. Yalnız BİLGİ AMAÇLI toplam için; hesap hiçbir yerde bunu saklamaz. */
  const [kurlar, setKurlar] = useState<Record<string, string>>({});

  /** TRY DIŞI para birimleri — kur kutusu yalnız gerçekten gerekliyse çıksın. */
  const dovizler = useMemo(
    () => (veri?.satisToplam ?? []).map((x) => x.paraBirimi).filter((p) => p !== "TRY"),
    [veri],
  );

  /**
   * BİLGİ AMAÇLI TL karşılığı. Kuru girilmemiş döviz TOPLAMA KATILMAZ ve kaç tanesinin
   * eksik olduğu söylenir — eksik kurla hesaplanmış bir "toplam kâr" yanıltıcı olurdu.
   */
  const tl = useMemo(() => {
    if (!veri) return null;
    let satis = 0, eksikDoviz = 0;
    for (const s of veri.satisToplam) {
      if (s.paraBirimi === "TRY") { satis += s.tutar; continue; }
      const k = Number((kurlar[s.paraBirimi] ?? "").replace(",", "."));
      if (Number.isFinite(k) && k > 0) satis += s.tutar * k; else eksikDoviz++;
    }
    return { satis, kar: satis - veri.maliyetToplam, eksikDoviz };
  }, [veri, kurlar]);

  const govde = () => {
    if (q.isLoading) return <div className="oca-msg">Mali analiz hesaplanıyor…</div>;
    if (q.isError) return <div className="oca-msg err" role="alert"><Icon name="alertTriangle" size={14} />{q.error.message}</div>;
    if (!veri) return null;

    return (
      <>
        {/* ── EKSİK HESAP UYARISI: en üstte, çünkü altındaki her rakamı etkiliyor ── */}
        {veri.eksikHesap ? (
          <div className="oca-msg err" role="alert">
            <Icon name="alertTriangle" size={15} />
            <span>
              <b>Bu analiz EKSİK.</b> {veri.fiyatsizKalemler.length} kalemin maliyeti bilinmiyor
              (fiyatı girilmemiş ham madde ya da reçetesi olmayan ürün). Maliyet toplamı bir
              <b> alt sınırdır</b>; gerçek maliyet daha yüksek, kâr daha düşüktür.
            </span>
          </div>
        ) : null}

        {/* ── KPI ── */}
        <div className="dash-kpi-grid oca-kpi">
          <div className="dash-kpi" style={{ ["--kpi-color" as string]: "var(--info)" }}>
            <div className="head"><span className="lbl">Satış</span></div>
            <div className="oca-para">
              {veri.satisToplam.length === 0 ? <span className="oca-yok">fiyat yok</span> : veri.satisToplam.map((x) => (
                <div key={x.paraBirimi} className="oca-para-sat">
                  <span className="v mono">{fmtPara(x.tutar)}</span>
                  <span className="p">{SEMBOL[x.paraBirimi] ?? x.paraBirimi}</span>
                </div>
              ))}
            </div>
            <div className="sub">para birimi bazında — toplanmaz</div>
          </div>

          <div className="dash-kpi" style={{ ["--kpi-color" as string]: "var(--warning)" }}>
            <div className="head"><span className="lbl">Maliyet</span></div>
            <div className="val">{fmtPara(veri.maliyetToplam)}<span className="unit">₺</span></div>
            <div className="sub">{veri.eksikHesap ? "⚠ alt sınır — eksik kalem var" : "alış fiyat listesi"}</div>
          </div>

          {/**
            * KÂR KARTI — kur girildiyse onu KULLANIR.
            *
            * Kullanıcı kuru girip alttaki kartta kârı gördüğü hâlde bu KPI "—" kalıyordu
            * (29 Ağu 2026 geri bildirimi): aynı sayfada aynı soruya bir yerde cevap var,
            * öbüründe yok. Artık tek kaynak: `kurluKar` varsa o, yoksa TL satırların kârı.
            *
            * ⚠ KUR TAMAMLANMADAN KULLANILMAZ. Eksik kurda `tl.satis` yalnız TL satışı içerir
            * ama `maliyetToplam` TÜM satırların maliyetidir — ikisini çıkarmak uydurma bir
            * zarar üretirdi. Bu yüzden koşul `eksikDoviz === 0`.
            * ⚠ Kur ile hesaplanan rakam BİLGİ AMAÇLIDIR; kart bunu etiketinde ve alt satırında
            * söyler (CLAUDE.md: dövizli tutar tek rakama indirgenmez).
            */}
          {(() => {
            const kurTamam = !!tl && dovizler.length > 0 && tl.eksikDoviz === 0;
            const kurluKar = kurTamam ? tl!.kar : null;
            const kar = kurluKar ?? veri.tryKar;
            const marj = kurTamam && tl!.satis > 0 ? (tl!.kar / tl!.satis) * 100 : null;
            const etiket = kurluKar != null ? "Kâr (kur ile)" : "Kâr (TL satırlar)";
            const alt = kurluKar != null
              ? `${dovizler.map((d) => `1 ${SEMBOL[d] ?? d} = ${kurlar[d]} ₺`).join(" · ")}${marj != null ? ` · %${fmtYuzde(marj)}` : ""} · bilgi amaçlı`
              : kar == null ? "TL satır yok — kur girin" : "yalnız TL satılan satırlar";
            return (
              <div className="dash-kpi" style={{ ["--kpi-color" as string]: (kar ?? 0) >= 0 ? "var(--success)" : "var(--danger-text)" }}>
                <div className="head"><span className="lbl">{etiket}</span></div>
                <div className="val" style={kar != null && kar < 0 ? { color: "var(--danger-text)" } : undefined}>
                  {kar == null ? "—" : fmtPara(kar)}<span className="unit">₺</span>
                </div>
                <div className="sub" title={alt}>{alt}</div>
              </div>
            );
          })()}

          <div className="dash-kpi" style={{ ["--kpi-color" as string]: "var(--primary)" }}>
            <div className="head"><span className="lbl">Kalem</span></div>
            <div className="val">{veri.satirlar.length}<span className="unit">satır</span></div>
            <div className="sub">{veri.fiyatsizKalemler.length > 0 ? `${veri.fiyatsizKalemler.length} kalem fiyatsız` : "tüm kalemler fiyatlı"}</div>
          </div>
        </div>

        {/* ── MANUEL KUR (bilgi amaçlı) ── */}
        {dovizler.length > 0 ? (
          <div className="d-card oca-kur">
            {/* Kur girişi BAŞLIK ŞERİDİNDE (kullanıcı kararı, 29 Ağu 2026): gövde yalnız sonuçları
                taşır, kart kısalır. Birden çok döviz varsa alanlar şeritte yan yana dizilir ve
                dar ekranda alt satıra sarar. */}
            <div className="d-card-head">
              <div className="left">
                <span className="t"><Icon name="banknote" size={12} /> Kur ile TL Karşılığı</span>
                <span className="s">bilgi amaçlı — hiçbir yere kaydedilmez</span>
              </div>
              <div className="oca-kur-giris">
                {dovizler.map((p) => (
                  <label key={p} className="oca-kur-alan">
                    <span>1 {SEMBOL[p] ?? p} =</span>
                    <input
                      type="text" inputMode="decimal" className="form-input" placeholder="0,00"
                      value={kurlar[p] ?? ""} onChange={(e) => setKurlar((s) => ({ ...s, [p]: e.target.value }))}
                      aria-label={`${p} kuru`}
                    />
                    <span>₺</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="d-card-body oca-kur-body">
              {tl ? (
                <div className="oca-kur-sonuc">
                  {tl.eksikDoviz > 0 ? (
                    <span className="oca-yok">{tl.eksikDoviz} para biriminin kuru girilmedi — toplama katılmadı.</span>
                  ) : null}
                  <div><span className="l">Satış (TL)</span><b className="mono">{fmtPara(tl.satis)} ₺</b></div>
                  <div><span className="l">Maliyet (TL)</span><b className="mono">{fmtPara(veri.maliyetToplam)} ₺</b></div>
                  <div className="grand">
                    <span className="l">Kâr (TL)</span>
                    <b className="mono" style={{ color: tl.kar >= 0 ? "var(--success)" : "var(--danger-text)" }}>
                      {fmtPara(tl.kar)} ₺
                      {tl.satis > 0 ? <span className="oca-marj"> %{fmtYuzde((tl.kar / tl.satis) * 100)}</span> : null}
                    </b>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* ── SATIR BAZINDA MALİYET & KÂR ── */}
        <div className="d-card">
          <div className="d-card-head">
            <div className="left">
              <span className="t"><Icon name="sigma" size={12} /> Satır Bazında Maliyet & Kâr</span>
              <span className="s">{veri.maliyetTabani}</span>
            </div>
          </div>
          <div className="d-card-body flush">
            <div className="oca-scroll">
              <table className="oca-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 34 }} />
                    <th>Ürün</th>
                    <th className="r">Miktar</th>
                    <th className="r">Satış Br.</th>
                    <th className="r">Satış Tutarı</th>
                    <th className="r">Br. Maliyet</th>
                    <th className="r">Maliyet</th>
                    <th className="r">Kâr</th>
                    <th className="r">Marj</th>
                  </tr>
                </thead>
                <tbody>
                  {veri.satirlar.map((s) => {
                    const sem = SEMBOL[s.satisParaBirimi ?? ""] ?? s.satisParaBirimi ?? "";
                    const kirilim = duzles(s.agac);
                    return (
                      // Fragment'ın KENDİSİ anahtar taşımalı: iki `<tr>` tek liste öğesi.
                      <Fragment key={s.orderLineId}>
                        <tr className={s.recetesiz ? "oca-eksik-satir" : undefined}>
                          <td>
                            {kirilim.length > 0 ? (
                              <button
                                type="button" className="oca-ac" aria-expanded={!!acik[s.orderLineId]}
                                aria-label={acik[s.orderLineId] ? "Kırılımı kapat" : "Reçete kırılımını aç"}
                                onClick={() => setAcik((a) => ({ ...a, [s.orderLineId]: !a[s.orderLineId] }))}
                              >
                                <Icon name={acik[s.orderLineId] ? "chevronDown" : "chevRight"} size={13} />
                              </button>
                            ) : null}
                          </td>
                          <td>
                            <b>{s.name}</b> <span className="oca-kod mono">{s.code}</span>
                            {s.recetesiz ? <span className="badge-pill b-danger oca-rozet"><Icon name="alertTriangle" size={9} />reçete yok</span> : null}
                          </td>
                          <td className="r mono">{fmtMiktar(s.miktar)} {s.uom}</td>
                          <td className="r mono">{s.satisBirimFiyat == null ? "—" : `${fmtPara(s.satisBirimFiyat)} ${sem}`}</td>
                          <td className="r mono">{s.satisTutar == null ? "—" : `${fmtPara(s.satisTutar)} ${sem}`}</td>
                          <td className="r mono">{s.maliyetBirim == null ? <span className="oca-yok">bilinmiyor</span> : `${fmtPara(s.maliyetBirim)} ₺`}</td>
                          <td className="r mono">{s.maliyetToplam == null ? <span className="oca-yok">bilinmiyor</span> : `${fmtPara(s.maliyetToplam)} ₺`}</td>
                          <td className="r mono" style={s.karTutar != null ? { color: s.karTutar >= 0 ? "var(--success)" : "var(--danger-text)", fontWeight: 700 } : undefined}>
                            {s.karTutar == null ? <span className="oca-yok" title="Satış para birimi maliyetten farklı — kur girin">kur gerekli</span> : `${fmtPara(s.karTutar)} ₺`}
                          </td>
                          <td className="r mono">{s.karYuzde == null ? "—" : `%${fmtYuzde(s.karYuzde)}`}</td>
                        </tr>
                        {acik[s.orderLineId] ? (
                          <tr key={`${s.orderLineId}-k`} className="oca-kirilim-satir">
                            <td colSpan={9}>
                              <div className="oca-kirilim">
                                <div className="oca-kirilim-bas">
                                  <Icon name="layers" size={12} />
                                  Reçete kırılımı — {kirilim.length} kalem, sipariş miktarına ölçeklenmiş
                                  {/* MÜKERRER TOPLAM ALGISINI KESEN NOT: grup satırı kendi alt kalemlerinin
                                      toplamıdır; sütun boyunca her şeyi toplamak çift sayar. Alttaki TOPLAM
                                      satırı da neyin toplandığını gösterir. */}
                                  <span className="oca-kirilim-not">
                                    grup satırları <b>yan toplamdır</b> — alt kalemleriyle birlikte toplanmaz
                                  </span>
                                </div>
                                <table className="oca-tbl ic">
                                  <thead>
                                    <tr>
                                      <th>Kalem</th>
                                      <th className="r">Miktar</th>
                                      <th className="r">Birim Fiyat</th>
                                      <th className="r">Tutar</th>
                                      <th className="r">Pay</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {kirilim.map((n) => (
                                      /* `data-sv`: derinlik → satır matlığı (CSS). Girinti tek başına
                                         hiyerarşiyi taşımıyordu, okuma zorlaşıyordu (kullanıcı bildirdi). */
                                      <tr
                                        key={n.anahtar}
                                        className={n.grupMu ? "oca-grup" : undefined}
                                        data-sv={Math.min(n.seviye, 4)}
                                      >
                                        {/* Girinti CSS'te: hücreye yalnız DERİNLİK verilir, piksel CSS hesaplar
                                            (CLAUDE.md: spacing inline `style` ile verilmez). */}
                                        <td className="oca-kalem" style={{ ["--sv" as string]: n.seviye }}>
                                          {n.grupMu ? <Icon name="boxes" size={11} /> : null} {n.name}
                                          <span className="oca-kod mono">{n.code}</span>
                                          {/* GRUP SATIRINDA DA rozet basılır: alt dalında fiyatsız kalem varsa
                                              "yan toplam" EKSİKTİR. Eskiden yalnız yaprakta basılıyordu; grup
                                              tertemiz bir rakam gösterip eksikliği gizliyordu. */}
                                          {n.eksikFiyat ? (
                                            <span className="badge-pill b-danger oca-rozet">
                                              <Icon name="alertTriangle" size={9} />{n.grupMu ? "eksik" : "fiyat yok"}
                                            </span>
                                          ) : null}
                                        </td>
                                        <td className="r mono">{fmtMiktar(n.miktar)} {n.uom}</td>
                                        <td className="r mono">{n.birimFiyat == null ? (n.grupMu ? "—" : <span className="oca-yok">yok</span>) : `${fmtPara(n.birimFiyat)} ₺`}</td>
                                        <td className="r mono">
                                          {n.tutar == null ? (
                                            <span className="oca-yok">bilinmiyor</span>
                                          ) : n.grupMu ? (
                                            /* GRUP = YAN TOPLAM. Etiketi rakamın yanında duruyor ki satır
                                               tek başına okunduğunda da "ek maliyet" sanılmasın. */
                                            <span className="oca-yan">
                                              <span className="et">yan toplam</span>
                                              <b>{fmtPara(n.tutar)} ₺</b>
                                            </span>
                                          ) : (
                                            `${fmtPara(n.tutar)} ₺`
                                          )}
                                        </td>
                                        {/* PAY'DA DA AYNI AYRIM. Düzeltme önce yalnız TUTAR sütununa uygulanmıştı;
                                            çift sayım algısı komşu sütuna taşınıyordu: grup %60 + yaprakları
                                            %40/%20 aynı biçimde basılınca sütun toplamı %100'ü aşıyor. Parantez
                                            muhasebede "toplama dahil değil" demektir. */}
                                        <td className="r mono oca-pay">
                                          {n.tutar == null || !s.maliyetToplam ? "—" : n.grupMu ? (
                                            <span className="oca-yan-pay" title="yan toplam — alt kalemleriyle birlikte toplanmaz">
                                              ({`%${fmtYuzde((n.tutar / s.maliyetToplam) * 100)}`})
                                            </span>
                                          ) : (
                                            `%${fmtYuzde((n.tutar / s.maliyetToplam) * 100)}`
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  {/**
                                    * TOPLAM SATIRI — "%100" ANCAK HESAP TAMSA.
                                    *
                                    * Arka uç toplamı yalnız TUTARI BİLİNEN kalemlerden kurar (order-cost.ts);
                                    * fiyatsız kalem varsa rakam bir ALT SINIRDIR. Eski hâli koyu-kalın "%100"
                                    * yazıyordu — sütunun en güçlü değeri, üstelik yanlış: eksik kümenin
                                    * tamamlanmışlık iddiası. Artık "≥" ile işaretlenir ve pay "—" olur.
                                    */}
                                  {s.maliyetToplam != null ? (() => {
                                    const eksikVar = s.fiyatsizKalemler.length > 0;
                                    const hicHesaplanamadi = !s.maliyetToplam;
                                    return (
                                      <tfoot>
                                        <tr>
                                          <td className="oca-kalem">
                                            Toplam{" "}
                                            <span className="oca-yok">
                                              {eksikVar ? "(yalnız fiyatı bilinen kalemler)" : "(kök kalemlerin toplamı)"}
                                            </span>
                                          </td>
                                          <td />
                                          <td />
                                          <td className="r mono">
                                            {hicHesaplanamadi
                                              ? <span className="oca-yok">hesaplanamadı</span>
                                              : <b>{eksikVar ? "≥ " : ""}{fmtPara(s.maliyetToplam)} ₺</b>}
                                          </td>
                                          <td className="r mono oca-pay">{eksikVar || hicHesaplanamadi ? "—" : "%100"}</td>
                                        </tr>
                                      </tfoot>
                                    );
                                  })() : null}
                                </table>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── FİYATI BİLİNMEYEN KALEMLER ── */}
        {veri.fiyatsizKalemler.length > 0 ? (
          <div className="d-card">
            <div className="d-card-head">
              <div className="left">
                <span className="t"><Icon name="alertTriangle" size={12} /> Maliyeti Bilinmeyen Kalemler</span>
                <span className="s">toplam bu kadar eksik</span>
              </div>
            </div>
            <div className="d-card-body flush">
              <table className="oca-tbl">
                <thead><tr><th>Kalem</th><th className="r">Miktar</th><th>Sebep</th></tr></thead>
                <tbody>
                  {veri.fiyatsizKalemler.map((k, i) => (
                    <tr key={`${k.code}-${i}`}>
                      <td>{k.name} <span className="oca-kod mono">{k.code}</span></td>
                      <td className="r mono">{fmtMiktar(k.miktar)} {k.uom}</td>
                      <td className="oca-yok">{k.recetesiz ? "Etkin reçetesi yok — kırılım yapılamadı" : "Stok kartında alış fiyatı girilmemiş"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="items-page oca-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <Link to="/orders" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Siparişler</Link>
        <span className="sep">/</span>
        <span className="cur">Mali Analiz</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">
            Mali Analiz
            {veri ? <span className="oca-baslik-kod mono"> {veri.siparis.code}</span> : null}
          </h1>
          <p className="sub">
            {veri ? `${veri.siparis.musteri} · maliyet ${veri.maliyetParaBirimi} · ` : ""}
            reçete kalemleri, grup kırılımı ve kâr analizi
          </p>
        </div>
        <button type="button" className="btn-md secondary" onClick={() => void q.refetch()} disabled={q.isFetching}>
          <Icon name="refresh" size={14} />{q.isFetching ? "Hesaplanıyor…" : "Yeniden hesapla"}
        </button>
      </div>

      {govde()}
    </div>
  );
}
